import { useEffect, useState } from 'react';
import { Box, Button, Layer } from 'grommet';
import { observer } from 'mobx-react-lite';
import { Card, CardBody, CardHeader, MaskedInput, Select } from '/src/components';
import { useUserStore } from '/src/context';
import { User } from '/src/lib/models';

type AddSalesCommissionModalProps = {
  onClose: () => void;
  onSubmit: (userId: number, percentRate: string) => Promise<void>;
  isLoading: boolean;
};

export const AddSalesCommissionModal: React.FC<AddSalesCommissionModalProps> = observer(
  ({ onClose, onSubmit, isLoading }) => {
    const userStore = useUserStore();
    const [userId, setUserId] = useState<number | null>(null);
    const [percentRate, setPercentRate] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
      userStore.fetchUsers({ is_internal: true });
    }, [userStore]);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      if (userId === null) return;

      try {
        setIsSubmitting(true);
        await onSubmit(userId, percentRate);
        onClose();
      } catch (error) {
        console.error('Error in onSubmit:', error);
      } finally {
        setIsSubmitting(false);
      }
    };

    const userOptions = userStore.users.map((user: User) => ({
      label: user.name,
      value: user.id,
    }));

    return (
      <Layer background="transparent">
        <Card width="100%" style={{ maxWidth: 480, minWidth: 320 }}>
          <CardHeader title="Add Sales Commission" />
          <CardBody pad={{ vertical: 'medium', horizontal: 'large' }}>
            <form onSubmit={handleSubmit}>
              <Box gap="medium">
                <Select
                  id="user_id"
                  name="user_id"
                  label="Salesperson"
                  value={userId}
                  onChange={(e) => setUserId(Number(e.value))}
                  options={userOptions}
                  placeholder="Select a salesperson"
                  required
                  disabled={isLoading}
                />

                <MaskedInput
                  label="Commission Rate (%)"
                  value={percentRate}
                  setValue={setPercentRate}
                  componentType="percent"
                  placeholder="0.00"
                  required
                />

                <Box direction="row" justify="end" gap="small">
                  <Button
                    label="Cancel"
                    onClick={onClose}
                    disabled={isLoading || isSubmitting}
                    style={{ fontWeight: 'normal' }}
                  />
                  <Button
                    type="submit"
                    primary
                    label={isSubmitting ? 'Adding...' : 'Add Commission'}
                    disabled={userId === null || !percentRate || isLoading || isSubmitting}
                    style={{ fontWeight: 'normal' }}
                  />
                </Box>
              </Box>
            </form>
          </CardBody>
        </Card>
      </Layer>
    );
  }
);
